import { CustomFieldValuesSection } from "@/shared/components/CustomFields";
import { TargetEntityType } from "@/shared/domain/customFields";
import { useReadyCustomFields } from "@/shared/domain/customFields/hooks";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import { FormSection } from "@/shared/foundation/primitives";

interface SessionCustomFieldsSectionProps {
  orgId: string;
  /** Draft custom-field values, owned by the create-session form. */
  values: Record<string, unknown>;
  onChange: (values: Record<string, unknown>) => void;
}

/**
 * Custom Fields card for the create-session page. Frames the shared
 * {@link CustomFieldValuesSection} in a `FormSection` so it matches the Name,
 * Files and Devices cards, which the shared section's header-driven layout
 * can't do.
 *
 * Gates the same way the shared section does — renders nothing when the
 * `release-custom-fields` flag is off or Sessions have no Ready fields — but
 * gates here too because only this component can hide the surrounding card,
 * so an empty state leaves no bare chrome.
 */
export function SessionCustomFieldsSection({
  orgId,
  values,
  onChange,
}: SessionCustomFieldsSectionProps) {
  const flagOn = useCheckFeatureFlag("release-custom-fields");
  const { data: readyFields } = useReadyCustomFields({
    entityType: TargetEntityType.Session,
    orgId,
    enabled: flagOn,
  });

  if (!flagOn || !readyFields?.length) {
    return null;
  }

  return (
    <FormSection title="Custom fields">
      <CustomFieldValuesSection
        entityType={TargetEntityType.Session}
        orgId={orgId}
        values={values}
        onChange={onChange}
      />
    </FormSection>
  );
}
